<script setup lang="ts">
import type { BtnSkin } from '@/ts/types/btn-types';

type Btn = {
    skin?: BtnSkin, 
    small?: boolean, 
    loc?: 'carousel' | 'lobby' | 'hp-sb' | 'gl-showcase' | 'fun-club-legend' | 'methods-page-mobile' | 'web-header' | 'popup',
    fontSize?: string,
};

const props = withDefaults(defineProps<Btn>(), {
    skin: 'none',
    small: false,
});
</script>

<template>
    <button :data-theme="skin" v-mobile :data-small="small" :data-location="loc">
        <div class="inner" v-mobile>
            <div class="shine" v-mobile></div>
            <div class="upper-layer" v-mobile></div>
            <div class="content" :data-small="small" v-mobile :style="{fontSize: fontSize ? `${fontSize} !important` : ''}">
                <slot></slot>
            </div>
        </div>
    </button>
</template>

<style lang="scss" scoped>
    @use '@/scss/shared';
    button{
        display: block;
        position: relative;
        font-family: 'Noto Sans';
        font-size: 20px;
        min-width: 160px;
        margin: 0 5px;
        outline: none;
        color: #fff;
        padding: 0 !important;
        background-color: transparent;
        border: none;
        transition: box-shadow .25s, opacity .25s;
        text-transform: uppercase;
        // #root[data-lang="el"] &{
        //     text-transform: capitalize;
        // }
        &[data-location='popup']{
            width: 100%;
            & > .inner > .content{
                padding: 0 !important;
            }
        }
        &[data-location='popup'],
        &[data-location='web-header']{
            text-transform: none;
            min-width: 0;
        }
        &[data-mobile]{
            font-size: 16px;
            min-width: 120px;
            &[data-location='web-header']{
                min-width: 0;
                & > .inner{
                    padding: 3px 10px !important;
                }
            }
            &[data-location='methods-page-mobile']{
                & > .inner{
                    padding: 5px 20px !important;
                }
            }
            &[data-location='fun-club-legend']{
                min-width: 0;
                flex: 1;
            }
            &[data-location='gl-showcase']{
                font-size: 13px;
                min-width: 100px;
                & > .inner{
                    padding: 5px 7px;
                    & > .content{
                        white-space: nowrap;
                    }
                }
            }
            &[data-location='carousel']{
                @media (min-height: 700px){
                    font-size: 20px;
                    & > .inner{
                        padding: 7px 20px;
                    }
                }
            }
            &[data-location='popup'],
            &[data-location='lobby']{
                @media (min-height: 700px){
                    font-size: 20px;
                    & > .inner{
                        padding: 7px 20px;
                    }
                }
            }
            &[data-location='hp-sb']{
                @media (min-height: 700px){
                    font-size: 20px;
                    & > .inner{
                        padding: 7px 10px;
                    }
                }
            }
        }
        &[data-small="true"]{
            min-width: 0 !important;
        }
        &:not([data-small="true"]){
            transform: skew(-10deg);
        }
        &[disabled]{
            opacity: .5;
            & > .inner{
                cursor: not-allowed !important;
            }
        }
        & > .inner{
            position: relative;
            padding: 7px 10px;
            width: auto;
            height: auto;
            text-shadow: 2px 2px 4px #0000007c;
            border: 2px solid;
            overflow: hidden;
            cursor: pointer;
            &[data-mobile]{
                padding: 4px 7px;
            }
            & > .shine{
                position: absolute;
                z-index: 2;
                top:0;
                left:0;
                width: 100%;
                height: 15px;
                background: linear-gradient(0deg, transparent 0%, #fff 100%);
                &[data-mobile]{
                    height: 7px;
                }
            }
            & > .upper-layer{
                position: absolute;
                width: 100%;
                height: 60%;
                top:-3px;
                left: 0;
                z-index: 1;
                box-shadow: 0 0 20px #000000ad;
                transform: rotate(3deg);
                overflow: hidden;
            }
            & > .content{
                position: relative;
                z-index: 10;
                font-weight: 1000 !important;
                &:not([data-small="true"]){
                    transition: transform .25s;
                    transform: scale(1) scaleY(1.3);
                }
                &[data-small="true"]{
                    padding: 0 !important;
                }
                &:not([data-mobile]){
                    padding: 0 10px;
                }
            }
        }
        &:after,
        &:before{
            position: absolute;
            display: block;
            content: "";
            width: 100%;
            height: 1px;
            left:0;
            z-index: 2;
        }
        &:before{
            top: 0;
        }
        &:after{
            bottom:1px;
        }
        &:not([data-mobile]):not([disabled]){
            &:hover{
                & .content:not([data-small="true"]){
                    transform: scale(1.05) scaleY(1.3);
                }
            }
        }

        @mixin btn-theme($skin, $color, $color-upper-layer-shine, $color-upper-layer-shadow, $site-key){
            &[data-theme="#{$skin}"]{
                $illumination-color: shared.theme-color($color, $site-key);
                @if $color-upper-layer-shine{
                    $illumination-color: shared.theme-color($color-upper-layer-shine, $site-key);
                }
                box-shadow: 0 0 10px 0 $illumination-color;
                &:not([disabled]):hover{
                    box-shadow: 0 0 20px 0 $illumination-color;
                }
                & > .inner{
                    background-color: shared.theme-color($color, $site-key);
                    border-color: shared.theme-color($color, $site-key);
                    & > .upper-layer{
                        background: shared.theme-color($color, $site-key);
                        @if $color-upper-layer-shine{
                            &:after{
                                display: block;
                                content: "";
                                position: absolute;
                                bottom:0;
                                height: 0;
                                left:10px;
                                width: calc(100% - 20px);
                                box-shadow: 0 0 10px 5px shared.theme-color($color-upper-layer-shine, $site-key);
                            }
                        }
                        @if $color-upper-layer-shadow{
                            box-shadow: 0 0 30px shared.theme-color($color-upper-layer-shadow, $site-key);
                        }
                    }
                }
                &:after,
                &:before{
                    $very-upper-shine-color: shared.theme-color('neutral_1', $site-key);
                    @if $color-upper-layer-shine{
                        $very-upper-shine-color: shared.theme-color($color-upper-layer-shine, $site-key);
                    }
                    background: linear-gradient(270deg, shared.theme-color($color, $site-key) 0%, $very-upper-shine-color 50%, shared.theme-color($color, $site-key) 100%);
                }
            }
        }

        @include shared.site() using($site-key){
            @include btn-theme('none', 'secondary_1', false, false, $site-key);
            @include btn-theme('secondary', 'comp1_5', 'comp2_3', false, $site-key);
            @include btn-theme('info', 'primary_4', 'primary_2', false, $site-key);
            @include btn-theme('money', 'comp5_4', 'comp5_1', false, $site-key);

            @if $site-key == "fund"{
                @include btn-theme('primary', 'comp3_3', false, false, $site-key);
                @include btn-theme('jackpot', 'comp7_3', false, 'comp7_7', $site-key);
            }
            @else{
                @include btn-theme('primary', 'comp7_3', false, 'comp7_7', $site-key);
                @include btn-theme('jackpot', 'comp7_3', false, 'comp7_7', $site-key);
            }
        }
    }
</style>